import { Box, Button, Stack, SvgIcon, SxProps, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { palette, spaces } from '../../../../assets/styles/themes/tokens';
import AIAssistantIconButton from '../../Dialog/AIAssistantIconButton';
import getTextTruncationSx from '../../../../utils/getTextTruncationSx';
import getMuiIcon from '../../../../utils/getMuiIcon';
import AIAssistantTextEditorExportButton, { TextEditorDownloadFormat } from './AIAssistantTextEditorExportButton';
import AIAssistantTextEditorCopyButton from './AIAssistantTextEditorCopyButton';
import { DEFAULT_HEADLINE_HEIGHT, TITLE_SAVE_DEBOUNCE_MS } from '../constants';
import {
  aiAssistantEditorContext,
  AIAssistantEditorMode,
} from '../../../../state/AIAssistantEditorProvider';
import { updateGeneratedDocument as updateGeneratedDocumentAction } from '../../../../actions/aiAssistant.actions';
import useAction from '../../../../utils/useAction';
import { debounce } from 'debounce';
import { muiInputFieldBorderStyle } from '../../../../constants';
import services from '../../../../services';
import { getCurrentConversationId, getGeneratedDocumentSync } from '../../../../reducers/aiAssistant.selectors';
import { useSelector } from 'react-redux';
import AutoSave from '../../../AutoSave/AutoSave';
import { AsyncActionStatus } from '../../../../actions/common.actionTypes';
import { aiAssistantContext, AIAssistantDisplayMode } from '../../../../state/AIAssistantContextProvider';
import usePodcastAudioDownload from './usePodcastAudioDownload';
import AnimatedEllipsis from '../Chat/Messages/AnimatedEllipsis';

const getTopBarStyles = (isPageMode: boolean): SxProps => isPageMode ? {
  display: 'flex',
  padding: `${spaces.sm} ${spaces.md} ${spaces.sm} ${spaces.xl}`,
  alignItems: 'center',
  gap: spaces.md,
  borderBottom: `1px solid ${palette.elevation.outlined}`,
  background: palette.background.paperElevation2,
} : {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: spaces.xs,
  paddingX: spaces.md,
  paddingY: spaces.sm,
  borderBottom: `1px solid ${palette.primary.contrast}`,
  height: `${DEFAULT_HEADLINE_HEIGHT}px`,
};

type Props = {
  // During podcast generation the editor isn't open yet (mode !== PODCAST), but the
  // bar must still show the podcast actions (disabled download) instead of the document ones.
  isPodcastLoading?: boolean;
};

const AIAssistantEditorTopBar = ({ isPodcastLoading = false }: Props) => {
  const { t } = useTranslation();
  const {
    editorData, mode, closeTextEditor, podcastDetails, updatePodcastTitle,
  } = useContext(aiAssistantEditorContext);
  const { displayMode } = useContext(aiAssistantContext);
  const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;
  const isPodcast = mode === AIAssistantEditorMode.PODCAST;
  const showPodcastActions = isPodcast || isPodcastLoading;
  const currentConversationId = useSelector(getCurrentConversationId);
  const truncateSx: SxProps = getTextTruncationSx(1);
  const [isEditMode, setIsEditMode] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);
  const updateGeneratedDocument = useAction(updateGeneratedDocumentAction);
  const [editedTitle, setEditedTitle] = useState(editorData.title);
  const syncData = useSelector(getGeneratedDocumentSync);

  const isEditable = Boolean(editorData.id);

  const debouncedUpdate = useRef(
    debounce((newTitle: string, documentId: string, updateFn: typeof updateGeneratedDocument) => {
      if (!documentId) return;
      updateFn({
        documentId,
        payload: { title: newTitle },
      });
    }, TITLE_SAVE_DEBOUNCE_MS)
  ).current;

  const toggleEditMode = () => {
    setIsEditMode(prev => !prev);

    setTimeout(() => {
      if (editInputRef.current) {
        setEditedTitle(editorData.title);
        editInputRef.current.focus();
      }
    }, 0);
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = event.target.value;
    setEditedTitle(newTitle);
    if (isPodcast) {
      updatePodcastTitle(newTitle);
    } else if (editorData.id) {
      debouncedUpdate(newTitle, editorData.id, updateGeneratedDocument);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      setIsEditMode(false);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsEditMode(false);
    }
  };

  useEffect(() => {
    if (!isEditMode) {
      setEditedTitle(editorData.title);
    }
  }, [editorData.title, isEditMode]);

  const feature = [isPodcast ? 'podcast generation' : 'document generation'];

  const onDownload = (format: TextEditorDownloadFormat) => {
    if (!currentConversationId) return;
    services.track.ampliTrackTextEditorFileExportedEvent({
      conversationId: currentConversationId,
      exportType: 'download',
      exportFormat: format,
      feature,
    });
  };

  const onPrint = () => {
    if (!currentConversationId) return;
    services.track.ampliTrackTextEditorFileExportedEvent({
      conversationId: currentConversationId,
      exportType: 'print',
      exportFormat: 'pdf',
      feature,
    });
  };

  const onShare = () => {
    if (!currentConversationId) return;
    services.track.ampliTrackTextEditorFileExportedEvent({
      conversationId: currentConversationId,
      exportType: 'share',
      feature,
    });
  };

  const { handleAudioDownload, isAudioDownloadDisabled } = usePodcastAudioDownload();

  const syncTargetId = isPodcast ? podcastDetails?.id : editorData.id;
  const isSyncing = Boolean(syncData.id
    && syncData.id === syncTargetId
    && syncData.status === AsyncActionStatus.LOADING
  );

  return (
    <Box
      data-testid='ai-assistant-text-editor-top-bar'
      sx={getTopBarStyles(isPageMode)}
    >
      <Stack direction='row' gap={spaces.xs} alignItems='center' flex={1} minWidth={0}>
        {isEditMode ? (
          <TextField
            data-testid='ai-assistant-text-editor-title-input'
            inputRef={editInputRef}
            fullWidth
            size='small'
            value={editedTitle}
            onChange={handleTitleChange}
            variant='outlined'
            inputProps={{ 'aria-label': 'Title' }}
            onBlur={toggleEditMode}
            onKeyDown={handleKeyDown}
            InputProps={{
              sx: {
                ...muiInputFieldBorderStyle,
              }
            }}
          />
        ) : (
          <Typography
            data-testid='ai-assistant-text-editor-title-text'
            variant='h6'
            sx={truncateSx}
          >
            {isPodcastLoading && !editorData.title ? (
              <>{t('Podcast is being created')}<AnimatedEllipsis /></>
            ) : (
              editorData.title
            )}
          </Typography>
        )}
        {(isEditable || isPodcast) && (
          <AIAssistantIconButton
            data-testid='text-editor-edit-button'
            icon='editPen'
            tooltip={t('Edit')}
            onClick={toggleEditMode}
            iconSize='large'
            size='small'
          />
        )}
      </Stack>
      {showPodcastActions ? (
        <Stack direction='row' gap={spaces.sm} alignItems='center'>
          <AutoSave status={isSyncing ? 'saving' : 'saved'} />
          <Button
            variant='contained'
            size='small'
            disabled={isAudioDownloadDisabled}
            onClick={handleAudioDownload}
            startIcon={<SvgIcon component={getMuiIcon('download')} aria-hidden='true' />}
          >
            {t('Audio herunterladen')}
          </Button>
        </Stack>
      ) : (
        <Stack direction='row' gap={spaces.xxs} alignItems='center'>
          {editorData.id && (
            <AutoSave status={isSyncing ? 'saving' : 'saved'} />
          )}
          <AIAssistantTextEditorCopyButton />
          <AIAssistantTextEditorExportButton
            id='ai-editor-export-button'
            onPrint={onPrint}
            onDownload={onDownload}
            onShare={onShare}
          />
          <AIAssistantIconButton
            data-testid='text-editor-close-button'
            icon='close'
            tooltip={t('Close')}
            onClick={closeTextEditor}
          />
        </Stack>
      )}
    </Box>
  );
};

export default AIAssistantEditorTopBar;
